import React, { useState, useEffect } from 'react';
import './RegionCombobox.css';

// Standard AWS regions (commercial + China), shown as "code — label" to avoid typos.
export const AWS_REGIONS: { code: string; label: string }[] = [
  { code: 'us-east-1', label: 'US East (N. Virginia)' },
  { code: 'us-east-2', label: 'US East (Ohio)' },
  { code: 'us-west-1', label: 'US West (N. California)' },
  { code: 'us-west-2', label: 'US West (Oregon)' },
  { code: 'af-south-1', label: 'Africa (Cape Town)' },
  { code: 'ap-east-1', label: 'Asia Pacific (Hong Kong)' },
  { code: 'ap-south-1', label: 'Asia Pacific (Mumbai)' },
  { code: 'ap-south-2', label: 'Asia Pacific (Hyderabad)' },
  { code: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  { code: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
  { code: 'ap-northeast-3', label: 'Asia Pacific (Osaka)' },
  { code: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
  { code: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
  { code: 'ap-southeast-3', label: 'Asia Pacific (Jakarta)' },
  { code: 'ap-southeast-4', label: 'Asia Pacific (Melbourne)' },
  { code: 'ca-central-1', label: 'Canada (Central)' },
  { code: 'ca-west-1', label: 'Canada West (Calgary)' },
  { code: 'eu-central-1', label: 'Europe (Frankfurt)' },
  { code: 'eu-central-2', label: 'Europe (Zurich)' },
  { code: 'eu-west-1', label: 'Europe (Ireland)' },
  { code: 'eu-west-2', label: 'Europe (London)' },
  { code: 'eu-west-3', label: 'Europe (Paris)' },
  { code: 'eu-north-1', label: 'Europe (Stockholm)' },
  { code: 'eu-south-1', label: 'Europe (Milan)' },
  { code: 'eu-south-2', label: 'Europe (Spain)' },
  { code: 'il-central-1', label: 'Israel (Tel Aviv)' },
  { code: 'me-south-1', label: 'Middle East (Bahrain)' },
  { code: 'me-central-1', label: 'Middle East (UAE)' },
  { code: 'sa-east-1', label: 'South America (São Paulo)' },
  { code: 'cn-north-1', label: 'China (Beijing)' },
  { code: 'cn-northwest-1', label: 'China (Ningxia)' },
];

interface RegionComboboxProps {
  value: string;
  onChange: (v: string) => void;
  id?: string;
  required?: boolean;
  disabled?: boolean;
}

// Searchable region combobox: type or paste a code; filtered suggestions drop directly below.
// Custom (not native <datalist>) to avoid browser autofill/positioning quirks inside modals.
const RegionCombobox: React.FC<RegionComboboxProps> = ({ value, onChange, id, required, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);

  useEffect(() => { setQuery(value); }, [value]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? AWS_REGIONS.filter((r) => r.code.toLowerCase().includes(q) || r.label.toLowerCase().includes(q))
    : AWS_REGIONS;
  const known = AWS_REGIONS.some((r) => r.code === value);

  return (
    <div className="region-combo">
      <input
        id={id}
        type="text"
        className="region-combo-input"
        value={query}
        autoComplete="off"
        placeholder="Region — type or paste, e.g. ap-southeast-1"
        required={required}
        disabled={disabled}
        onChange={(e) => { setQuery(e.target.value); onChange(e.target.value.trim()); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}
      />
      {open && filtered.length > 0 && (
        <ul className="region-combo-list">
          {filtered.map((r) => (
            <li
              key={r.code}
              className="region-combo-option"
              onMouseDown={(e) => { e.preventDefault(); onChange(r.code); setQuery(r.code); setOpen(false); }}
            >
              <strong>{r.code}</strong> <span>{r.label}</span>
            </li>
          ))}
        </ul>
      )}
      {value && !known && (
        <p className="region-warning">⚠ "{value}" isn't a standard AWS region code — double-check for typos.</p>
      )}
    </div>
  );
};

export default RegionCombobox;
